import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SidebarLayout, PageHeader, Card } from '../components/layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../hooks/use-auth';
import { useOrders } from '../hooks/use-orders';
import { useUserApprovals } from '../hooks/use-user-approvals';
import { useMinDurationFlag } from '../hooks/use-min-duration-flag';
import { OrderSummaryDto } from '../types';
import { formatCurrency, formatDate, sortableHeaderProps } from '../utils';

export function AllOrdersPage() {
  const { user, isAuthenticated, isAdmin, isSupport, isWorkflowAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Workflow filter from URL params (e.g. "Orders" button on the Workflow Management page) -
  // shows every order ever associated with the workflow regardless of status, unlike the
  // In Progress page's workflow filter which only shows orders actively mid-flight.
  const workflowIdFromUrl = searchParams.get('workflowId');
  const workflowNameFromUrl = searchParams.get('workflowName');

  // Get division ID from user approvals hook for consistency
  const { divisionId } = useUserApprovals();

  // WorkflowAdmin should see all orders, same as customer support
  const isAdminOrSupport = isAdmin || isSupport || isWorkflowAdmin;

  // Filter state
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    search: '',
    submitter: '',
    status: '',
    workflow: '',
    createdStartDate: '',
    createdEndDate: '',
    updatedStartDate: '',
    updatedEndDate: '',
    minAmount: '',
    maxAmount: '',
    sortBy: 'CreatedAt',
    sortOrder: 'desc' as 'asc' | 'desc'
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Debounced submitter filter
  const [submitterInput, setSubmitterInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, submitter: submitterInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [submitterInput]);

  // Debounced workflow filter
  const [workflowInput, setWorkflowInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, workflow: workflowInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [workflowInput]);

  // Debounced amount filters
  const [minAmountInput, setMinAmountInput] = useState('');
  const [maxAmountInput, setMaxAmountInput] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, minAmount: minAmountInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [minAmountInput]);
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, maxAmount: maxAmountInput, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [maxAmountInput]);

  // Fetch all orders (admin can see all orders)
  const { 
    data: ordersData, 
    isLoading, 
    isFetching,
    error, 
    refetch 
  } = useOrders(
    divisionId || '', 
    {
      page: filters.page,
      limit: filters.limit,
      search: filters.search || undefined,
      submitterId: filters.submitter || undefined,
      status: filters.status || undefined,
      workflowId: workflowIdFromUrl || undefined,
      workflowName: filters.workflow || undefined,
      startDate: filters.createdStartDate || undefined,
      endDate: filters.createdEndDate || undefined,
      updatedStartDate: filters.updatedStartDate || undefined,
      updatedEndDate: filters.updatedEndDate || undefined,
      minAmount: filters.minAmount ? Number(filters.minAmount) : undefined,
      maxAmount: filters.maxAmount ? Number(filters.maxAmount) : undefined,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder
    },
    !!divisionId && isAuthenticated && isAdminOrSupport
  );
  const showFetchingOverlay = useMinDurationFlag(isFetching && !isLoading);

  const orders = ordersData?.items || [];
  const totalCount = ordersData?.totalCount || 0;
  const currentPage = ordersData?.page || 1;
  const pageSize = ordersData?.pageSize || 50;
  const totalPages = ordersData?.totalPages || 1;

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1
    }));
  };

  const getSortIcon = (field: string) => {
    if (filters.sortBy !== field) return <i className="fa-solid fa-sort text-gray-500 dark:text-secondary-400 ml-2"></i>;
    return filters.sortOrder === 'asc' 
      ? <i className="fa-solid fa-sort-up text-blue-600 dark:text-blue-400 ml-2"></i>
      : <i className="fa-solid fa-sort-down text-blue-600 dark:text-blue-400 ml-2"></i>;
  };

  const handleViewOrder = (orderId: string) => {
    navigate(`/orders/${orderId}`);
  };

  const handleOpenInSimulator = (orderId: string) => {
    navigate(`/simulator?orderId=${orderId}`);
  };

  const getStatusBadgeVariant = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
    switch (status.toLowerCase()) {
      case 'submitted': return 'info';
      case 'pending': return 'info';
      case 'processing': return 'warning';
      case 'completed': return 'success';
      case 'partiallycompleted': return 'success';
      case 'cancelled': return 'error';
      case 'paused': return 'warning';
      case 'needsresolved': return 'error';
      case 'timeout': return 'error';
      case 'error': return 'error';
      case 'escalated': return 'warning';
      default: return 'default';
    }
  };

  const formatStatus = (status: string) => {
    // Convert camelCase to separate words
    return status
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/^./, str => str.toUpperCase());
  };

  if (!isAuthenticated) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <Card>
            <div className="p-6 text-center">
              <p className="text-gray-600 dark:text-secondary-400">Please sign in to view orders.</p>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (!isAdminOrSupport) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <Card>
            <div className="p-6 text-center">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Access Denied</h2>
              <p className="text-gray-600 dark:text-secondary-400">Only administrators can view all orders.</p>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  if (error) {
    return (
      <SidebarLayout>
        <div className="py-4">
          <PageHeader
            title="All Orders"
            subtitle="View all orders across all statuses in the division"
          />
          <Card>
            <div className="p-6 text-center">
              <p className="text-red-600 dark:text-red-400 mb-4">Error loading orders</p>
              <Button onClick={() => refetch()}>Try Again</Button>
            </div>
          </Card>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="py-4">
        <PageHeader
          title="All Orders"
          subtitle="View and manage all orders in the division"
          actions={
            <Button onClick={() => refetch()} variant="outline">
              <i className="fa-solid fa-refresh mr-2"></i>
              Refresh
            </Button>
          }
        />

        {workflowIdFromUrl && workflowNameFromUrl && (
          <div className="mb-4 flex items-center gap-2">
            <Badge variant="info" className="text-sm">
              <i className="fa-solid fa-filter mr-2"></i>
              Filtered by workflow: {decodeURIComponent(workflowNameFromUrl)}
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                searchParams.delete('workflowId');
                searchParams.delete('workflowName');
                setSearchParams(searchParams);
              }}
              className="text-sm"
            >
              <i className="fa-solid fa-times mr-1"></i>
              Clear Filter
            </Button>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-secondary-400">Total Orders</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2" data-testid="stat-total-orders">{totalCount}</p>
                </div>
                <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-full">
                  <i className="fa-solid fa-box text-blue-600 dark:text-blue-400 text-xl"></i>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-secondary-400">Current Page</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{currentPage}</p>
                </div>
                <div className="bg-green-100 dark:bg-green-900 p-3 rounded-full">
                  <i className="fa-solid fa-file text-green-600 dark:text-green-400 text-xl"></i>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-secondary-400">Total Pages</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{totalPages}</p>
                </div>
                <div className="bg-purple-100 dark:bg-purple-900 p-3 rounded-full">
                  <i className="fa-solid fa-layer-group text-purple-600 dark:text-purple-400 text-xl"></i>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-secondary-400">On This Page</p>
                  <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{orders.length}</p>
                </div>
                <div className="bg-orange-100 dark:bg-orange-900 p-3 rounded-full">
                  <i className="fa-solid fa-list text-orange-600 dark:text-orange-400 text-xl"></i>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Orders Table */}
        <Card>
          <div className="px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 dark:border-secondary-700">
            <div className="flex items-center gap-4">
              <Button onClick={() => refetch()} variant="outline" size="sm">
                <i className="fa-solid fa-refresh mr-2"></i>
                Refresh
              </Button>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600 dark:text-secondary-400">Page Size:</label>
                <select
                  value={filters.limit}
                  onChange={(e) => setFilters(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}
                  className="px-2 py-1 border border-gray-300 dark:border-secondary-600 rounded text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </div>
            </div>
            {(filters.search || filters.submitter || filters.status || filters.workflow || filters.createdStartDate || filters.createdEndDate || filters.updatedStartDate || filters.updatedEndDate || filters.minAmount || filters.maxAmount) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchInput('');
                  setSubmitterInput('');
                  setWorkflowInput('');
                  setMinAmountInput('');
                  setMaxAmountInput('');
                  setFilters(prev => ({ ...prev, search: '', submitter: '', status: '', workflow: '', createdStartDate: '', createdEndDate: '', updatedStartDate: '', updatedEndDate: '', minAmount: '', maxAmount: '', page: 1 }));
                }}
              >
                <i className="fa-solid fa-times mr-2"></i>
                Clear Filters
              </Button>
            )}
          </div>
          <div className="p-6 relative">
            {/* Loading overlay during filter changes */}
            {showFetchingOverlay && (
              <div className="absolute inset-0 bg-white/60 dark:bg-secondary-800/60 flex items-start justify-center pt-16 z-10 rounded-b-lg">
                <div className="bg-white dark:bg-secondary-800 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3">
                  <i className="fa-solid fa-spinner fa-spin text-blue-600 dark:text-blue-400"></i>
                  <span className="text-sm font-medium text-gray-700 dark:text-secondary-200">Updating results...</span>
                </div>
              </div>
            )}
            {isLoading ? (
              <div className="text-center py-12">
                <i className="fa-solid fa-spinner fa-spin text-4xl text-blue-600 dark:text-blue-400 mb-4"></i>
                <p className="text-gray-600 dark:text-secondary-400">Loading orders...</p>
              </div>
            ) : (
              <>
                {/* Pagination - Top */}
                {orders.length > 0 && totalPages > 1 && (
                  <div className="mb-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-secondary-200">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                      >
                        <i className="fa-solid fa-chevron-left mr-2"></i>
                        Previous
                      </Button>
                      
                      <div className="flex items-center space-x-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                              variant={currentPage === pageNum ? 'primary' : 'outline'}
                              size="sm"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Next
                        <i className="fa-solid fa-chevron-right ml-2"></i>
                      </Button>
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                    <thead className="bg-gray-50 dark:bg-secondary-900">
                      {/* Column Headers with Sorting */}
                      <tr>
                        <th 
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          {...sortableHeaderProps(() => handleSort('OrderNumber'), filters.sortBy === 'OrderNumber', filters.sortOrder)}
                        >
                          <div className="flex items-center">
                            Order Number
                            {getSortIcon('OrderNumber')}
                          </div>
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Submitter
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Workflow
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th 
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          {...sortableHeaderProps(() => handleSort('TotalAmount'), filters.sortBy === 'TotalAmount', filters.sortOrder)}
                        >
                          <div className="flex items-center">
                            Amount
                            {getSortIcon('TotalAmount')}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          {...sortableHeaderProps(() => handleSort('CreatedAt'), filters.sortBy === 'CreatedAt', filters.sortOrder)}
                        >
                          <div className="flex items-center">
                            Created
                            {getSortIcon('CreatedAt')}
                          </div>
                        </th>
                        <th 
                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-secondary-600"
                          {...sortableHeaderProps(() => handleSort('UpdatedAt'), filters.sortBy === 'UpdatedAt', filters.sortOrder)}
                        >
                          <div className="flex items-center">
                            Updated
                            {getSortIcon('UpdatedAt')}
                          </div>
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                      
                      {/* Filter Row */}
                      <tr className="bg-white dark:bg-secondary-800 border-t border-gray-200 dark:border-secondary-700">
                        <th className="px-4 py-2">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="search-input"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          />
                        </th>
                        <th className="px-4 py-2">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={submitterInput}
                            onChange={(e) => setSubmitterInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="submitter-input"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          />
                        </th>
                        <th className="px-4 py-2">
                          <input
                            type="text"
                            placeholder="Filter..."
                            value={workflowInput}
                            onChange={(e) => setWorkflowInput(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          />
                        </th>
                        <th className="px-4 py-2">
                          <select
                            value={filters.status}
                            onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value, page: 1 }))}
                            onClick={(e) => e.stopPropagation()}
                            data-testid="status-filter"
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                          >
                            <option value="">All</option>
                            <option value="Submitted">Submitted</option>
                            <option value="Pending">Pending</option>
                            <option value="Processing">Processing</option>
                            <option value="Paused">Paused</option>
                            <option value="NeedsResolved">Needs Resolved</option>
                            <option value="Escalated">Escalated</option>
                            <option value="Completed">Completed</option>
                            <option value="PartiallyCompleted">Partially Completed</option>
                            <option value="Cancelled">Cancelled</option>
                            <option value="Timeout">Timeout</option>
                            <option value="Error">Error</option>
                          </select>
                        </th>
                        <th className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <input
                              type="number"
                              placeholder="Min"
                              value={minAmountInput}
                              onChange={(e) => setMinAmountInput(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                            <input
                              type="number"
                              placeholder="Max"
                              value={maxAmountInput}
                              onChange={(e) => setMaxAmountInput(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              title="Created from"
                              value={filters.createdStartDate}
                              onChange={(e) => setFilters(prev => ({ ...prev, createdStartDate: e.target.value, page: 1 }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                            <input
                              type="date"
                              title="Created to"
                              value={filters.createdEndDate}
                              onChange={(e) => setFilters(prev => ({ ...prev, createdEndDate: e.target.value, page: 1 }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <input
                              type="date"
                              title="Updated from"
                              value={filters.updatedStartDate}
                              onChange={(e) => setFilters(prev => ({ ...prev, updatedStartDate: e.target.value, page: 1 }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                            <input
                              type="date"
                              title="Updated to"
                              value={filters.updatedEndDate}
                              onChange={(e) => setFilters(prev => ({ ...prev, updatedEndDate: e.target.value, page: 1 }))}
                              onClick={(e) => e.stopPropagation()}
                              className="w-full px-1 py-1 text-xs border border-gray-300 dark:border-secondary-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
                            />
                          </div>
                        </th>
                        <th className="px-4 py-2">
                          {/* No filter for actions column */}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                      {orders.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center py-12">
                            <i className="fa-solid fa-inbox text-4xl text-gray-300 dark:text-secondary-600 mb-4 block"></i>
                            <p className="text-gray-600 dark:text-secondary-400 mb-2">No orders found</p>
                            {(filters.search || filters.submitter || filters.status || filters.workflow || filters.createdStartDate || filters.createdEndDate || filters.updatedStartDate || filters.updatedEndDate || filters.minAmount || filters.maxAmount) && (
                              <p className="text-sm text-gray-500 dark:text-secondary-400">Try adjusting your search or filters</p>
                            )}
                          </td>
                        </tr>
                      ) : orders.map((order: OrderSummaryDto) => (
                        <tr key={order.id} className="hover:bg-gray-50 dark:hover:bg-secondary-700" data-testid={`order-row-${order.id}`}>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <a
                              className="font-mono text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                              href={`/orders/${order.id}`}
                              onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                              title="Click to view order details"
                              data-testid={`order-number-${order.id}`}
                            >
                              {order.orderNumber}
                            </a>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">{order.submitterName || 'Unknown'}</div>
                            {order.submitterEmail && (
                              <div className="text-sm text-gray-500 dark:text-secondary-400">{order.submitterEmail}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {order.workflowName && order.workflowId ? (
                              isSupport ? (
                                <span className="text-sm text-gray-700 dark:text-secondary-200">{order.workflowName}</span>
                              ) : (
                                <button
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 hover:underline cursor-pointer"
                                  onClick={() => navigate(`/workflows/${order.divisionId}/${order.workflowId}/edit`)}
                                  title="View workflow details"
                                >
                                  {order.workflowName}
                                </button>
                              )
                            ) : (
                              <span className="text-sm text-gray-400 dark:text-secondary-500">No workflow</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <Badge variant={getStatusBadgeVariant(order.status)}>
                              {formatStatus(order.status)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {order.originalTotalAmount && order.originalTotalAmount !== order.totalAmount ? (
                              <div className="flex flex-col items-start gap-0.5">
                                <span className="line-through text-gray-400 dark:text-secondary-500 text-xs">
                                  {formatCurrency(order.originalTotalAmount)}
                                </span>
                                <span className="text-blue-600 dark:text-blue-400 font-semibold text-sm">
                                  {formatCurrency(order.totalAmount)}
                                </span>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-900 dark:text-white">
                                {formatCurrency(order.totalAmount)}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {formatDate(order.createdAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {formatDate(order.updatedAt)}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <a
                                className="text-blue-600 dark:text-blue-400 hover:text-blue-800 p-1"
                                href={`/orders/${order.id}`}
                                onClick={(e) => { e.preventDefault(); handleViewOrder(order.id); }}
                                title="View Details"
                              >
                                <i className="fa-solid fa-eye"></i>
                              </a>
                              {isAdmin && (
                                <button 
                                  className="text-purple-600 dark:text-purple-400 hover:text-purple-800 p-1"
                                  onClick={() => handleOpenInSimulator(order.id)}
                                  title="View in Simulator"
                                >
                                  <i className="fa-solid fa-flask"></i>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {orders.length > 0 && totalPages > 1 && (
                  <div className="mt-6 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-secondary-200">
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                      >
                        <i className="fa-solid fa-chevron-left mr-2"></i>
                        Previous
                      </Button>
                      
                      <div className="flex items-center space-x-2">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <Button
                              key={pageNum}
                              onClick={() => setFilters(prev => ({ ...prev, page: pageNum }))}
                              variant={currentPage === pageNum ? 'primary' : 'outline'}
                              size="sm"
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>
                      
                      <Button
                        onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                      >
                        Next
                        <i className="fa-solid fa-chevron-right ml-2"></i>
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
      </div>
    </SidebarLayout>
  );
}
